// src/components/separation/resignationlist.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react';
import { ResignationRequest } from '../../types/separation';
import { getResignations } from '../../api/separation';
import ResignationModal from './resignationmodal';
import { statusBadge, employeeLabel, fmtDate } from './uiHelpers';
import { useAccess } from '../../contexts/AccessContext';

interface ResignationListProps {
  onViewChecklist?: (type: string, id: string) => void;
  onCreateSettlement?: (type: string, id: string, employeeId: string) => void;
  userRole?: string;
  onChanged?: () => void;
}

// Approval tiers — kept in sync with the backend action_resignation chain so
// the Review button only shows when THIS role is the next approver.
const MANAGER_ROLES = ['manager', 'supervisor'];
const HR_ROLES = ['hr', 'hr head', 'hr admin'];
const ADMIN_ROLES = ['admin', 'superadmin', 'founder', 'director'];

const ResignationList: React.FC<ResignationListProps> = ({
  onViewChecklist,
  onCreateSettlement,
  userRole = 'employee',
  onChanged,
}) => {
  const role = (userRole || 'employee').toLowerCase();
  const { canManage } = useAccess();
  const [resignations, setResignations] = useState<ResignationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedResignation, setSelectedResignation] = useState<ResignationRequest | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'action' | 'withdraw' | 'earlyRelease'>('action');
  const limit = 20;

  const loadResignations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getResignations({ page, limit });
      setResignations(response.data || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Failed to load resignations:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadResignations();
  }, [loadResignations]);

  const canAction = (status: string): boolean => {
    if (MANAGER_ROLES.includes(role)) return status === 'PENDING';
    if (HR_ROLES.includes(role)) return status === 'MANAGER_APPROVED';
    if (ADMIN_ROLES.includes(role)) return status === 'HR_APPROVED';
    return false;
  };

  const canWithdraw = (status: string): boolean =>
    !['COMPLETED', 'ADMIN_APPROVED', 'ADMIN_REJECTED', 'WITHDRAWN'].includes(status);

  // Withdrawing someone else's resignation is a plain manage right — decided by
  // Access Management ("separation" panel at Manage), not by role. The
  // canAction() chain above stays role-based on purpose: it encodes WHICH
  // approval stage a role owns, which the access profile has no notion of.
  const isElevated = canManage("separation");
  const totalPages = Math.ceil(total / limit) || 1;

  const openModal = (r: ResignationRequest, type: 'action' | 'withdraw' | 'earlyRelease') => {
    setSelectedResignation(r);
    setModalType(type);
    setModalOpen(true);
  };

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Resignation Requests</h3>
            <p className="text-xs text-gray-500 mt-0.5">Total: {total}</p>
          </div>
        </div>

        <div className="overflow-auto max-h-[70vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-gray-500">
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Employee</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Resigned On</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Last Working Day</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Status</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase">Early Release</th>
                <th className="sticky top-0 z-10 bg-gray-50 px-6 py-3 text-xs font-medium uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {resignations.map((r) => {
                const badge = statusBadge(r.status);
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{employeeLabel(r)}</div>
                      <div className="text-xs text-gray-400">
                        {r.employee_code || `#${r.id.slice(0, 8)}`}
                        {r.designation ? ` · ${r.designation}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{fmtDate(r.resignation_date)}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {fmtDate(r.actual_last_day || r.requested_last_day)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {r.early_release_requested ? (
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            r.early_release_approved
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {r.early_release_approved ? 'Approved' : 'Requested'}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap space-x-3">
                      {canAction(r.status) && (
                        <button onClick={() => openModal(r, 'action')} className="text-emerald-600 hover:text-emerald-800 font-medium">
                          Review
                        </button>
                      )}
                      {isElevated && canWithdraw(r.status) && (
                        <button onClick={() => openModal(r, 'withdraw')} className="text-red-600 hover:text-red-800">
                          Withdraw
                        </button>
                      )}
                      <button onClick={() => onViewChecklist?.('RESIGNATION', r.id)} className="text-blue-600 hover:text-blue-800">
                        Checklist
                      </button>
                      {(r.status === 'ADMIN_APPROVED' || r.status === 'COMPLETED') && (
                        <button
                          onClick={() => onCreateSettlement?.('RESIGNATION', r.id, r.employee_id)}
                          className="text-purple-600 hover:text-purple-800"
                        >
                          Settlement
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {resignations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <ClipboardList className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    No resignation requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="gt-btn is-ghost is-sm disabled:opacity-50">
              <ChevronLeft size={14} /> Previous
            </button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="gt-btn is-ghost is-sm disabled:opacity-50">
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      <ResignationModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedResignation(null);
        }}
        resignation={selectedResignation}
        modalType={modalType}
        userRole={role}
        onSuccess={() => {
          loadResignations();
          onChanged?.();
        }}
      />
    </>
  );
};

export default ResignationList;
